import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CitizenLayout from "../../components/CitizenLayout";
import RequireAuth from "../../components/RequireAuth";
import {
  ContextStrip,
  LoadingScreen,
  PageHeader,
  StepBreadcrumb,
  StickyFooter,
  SummaryRow,
} from "../../components/CitizenUi";
import { useCitizenSession } from "../../session/b2cSession";
import * as b2cApi from "../../api/b2cApi";
import {
  launchBookingPayment,
  useCashfreeReturn,
} from "../../hooks/usePayment";
import "../../citizen.css";

var STEPS = ["Choose slot", "Review & pay", "Confirmed"];

/* Format an API datetime string e.g. "2026-08-03 10:30:00" → "Mon, 3 Aug · 10:30 AM" */
function fmtDt(apiStr) {
  if (!apiStr) return "—";
  var d = new Date(String(apiStr).replace(" ", "T"));
  if (isNaN(d.getTime())) return apiStr;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) +
    " · " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function normalizePrice(body) {
  if (!body || typeof body !== "object") return null;
  var val = body.total_amount != null ? body.total_amount : body.round_amount;
  if (val == null && body.payload) {
    val = body.payload.total_amount != null ? body.payload.total_amount : body.payload.round_amount;
  }
  if (val == null) val = body.amount;
  if (val === "-1" || val === -1) return null;
  return val;
}

function SummaryContent() {
  var session = useCitizenSession();
  var history = useHistory();
  var [draft,        setDraft]        = useState(null);
  var [coupon,       setCoupon]       = useState("");
  var [couponApplied, setCouponApplied] = useState(null);
  var [price,        setPrice]        = useState(null);
  var [error,        setError]        = useState("");
  var [busy,         setBusy]         = useState(false);
  var [couponBusy,   setCouponBusy]   = useState(false);

  /* Load draft from sessionStorage */
  useEffect(function () {
    try {
      var raw = sessionStorage.getItem("citizen_booking_draft");
      if (!raw) { history.replace("/citizen"); return; }
      var parsed = JSON.parse(raw);
      setDraft(parsed);
      var base = normalizePrice(parsed.availability);
      if (base != null) setPrice({ base: base, total: base });
    } catch (_) {
      history.replace("/citizen");
    }
  }, [history]);

  /* Handle return from Cashfree payment gateway via shared hook */
  useCashfreeReturn({
    active: true,
    token: session.token,
    setBusy: setBusy,
    onSuccess: function () {
      try { sessionStorage.removeItem("citizen_booking_draft"); } catch (_) {}
      history.replace("/citizen/bookings?confirmed=1");
    },
    onError: function (e) {
      setError(e.message || "Could not confirm booking after payment.");
    },
  });

  function calcDiscount(body) {
    var d = body.discount != null
      ? body.discount
      : (body.payload && body.payload.discount != null ? body.payload.discount : null);
    if (d != null) return Number(d);
    var base  = normalizePrice(draft && draft.availability);
    var after = normalizePrice(body);
    return (base != null && after != null) ? Number(base) - Number(after) : 0;
  }

  var applyCoupon = useCallback(function () {
    if (!draft || !coupon.trim()) return;
    setError("");
    setCouponBusy(true);
    b2cApi
      .applyCoupon(
        {
          coupon_code:            coupon.trim(),
          parking_table_id:       draft.parkingTableId,
          booking_start_datetime: draft.start,
          booking_end_datetime:   draft.end,
        },
        session.token
      )
      .then(function (body) {
        if (body && body.success === false) throw new Error(body.message || "Coupon not applicable");
        var after = normalizePrice(body);
        if (after == null) throw new Error("Could not apply coupon.");
        var base     = price ? price.base : normalizePrice(draft.availability);
        var discount = calcDiscount(body);
        setPrice({ base: base, total: after, discount: discount });
        setCouponApplied(coupon.trim());
      })
      .catch(function (e) {
        setCouponApplied(null);
        var base = normalizePrice(draft.availability);
        setPrice({ base: base, total: base });
        setError(e.message || "Coupon invalid or expired");
      })
      .then(function () {
        setCouponBusy(false);
      });
  }, [coupon, draft, session.token, price]);

  var removeCoupon = useCallback(function () {
    setCoupon("");
    setCouponApplied(null);
    if (draft) {
      var base = normalizePrice(draft.availability);
      setPrice({ base: base, total: base });
    }
  }, [draft]);

  var pay = useCallback(function () {
    if (!draft) return;
    setBusy(true);
    setError("");
    var payload = {
      parking_table_id:       draft.parkingTableId,
      vehicle_id:             draft.vehicleId,
      booking_start_datetime: draft.start,
      booking_end_datetime:   draft.end,
    };
    if (couponApplied) payload.coupon_code = couponApplied;
    b2cApi
      .confirmBooking(payload, session.token)
      .then(function (body) {
        if (body && body.success === false) throw new Error(body.message || "Booking failed");
        var bookingId = body.booking_id || (body.payload && body.payload.booking_id);
        if (!bookingId) throw new Error("Booking ID not returned from server");
        var sessionId =
          body.cashfree_token ||
          body.payment_session_id ||
          body.cashfree_session_id ||
          (body.payload && (body.payload.cashfree_token || body.payload.payment_session_id));
        if (!sessionId || String(sessionId).trim() === "") {
          throw new Error("Payment session could not be created. Please try again.");
        }
        // Use shared helper — stores bookingId and returns URL before redirect
        return launchBookingPayment(sessionId, bookingId, window.location.pathname);
      })
      .catch(function (e) {
        setError(e.message || "Payment could not be initiated.");
        setBusy(false);
      });
  }, [draft, couponApplied, session.token]);

  if (!draft) {
    return (
      <CitizenLayout title="Summary" hideNav backTo="/citizen">
        <LoadingScreen message="Preparing summary…" />
      </CitizenLayout>
    );
  }

  var total = (price && price.total != null ? price.total : null) || normalizePrice(draft.availability);

  return (
    <CitizenLayout title="Review & Pay" hideNav backTo={"/citizen/book/" + draft.parkingTableId}>
      <div className="citizen-body--form-only">

        {/* Step indicator */}
        <StepBreadcrumb steps={STEPS} current={2} />

        {/* Parking context */}
        {draft.parkingName ? (
          <ContextStrip icon="🅿️" name={draft.parkingName} meta={draft.parkingAddress} />
        ) : null}

        <PageHeader
          eyebrow="Almost there"
          title="Lock this bay"
          subtitle="One tap and the pass is yours."
        />

        {error ? <div className="citizen-error">{error}</div> : null}

        {/* ── Booking summary card ── */}
        <div className="cz-ticket cz-ticket--preview">
          <div className="cz-ticket__top">
            <div>
              <small>ParkAdda pass preview</small>
              <h2>{draft.parkingName || "Parking"}</h2>
              <p>{draft.vehicleNumber || "Vehicle"}{draft.vehicleType ? " · " + draft.vehicleType : ""}</p>
            </div>
            {total != null ? <strong>₹{total}</strong> : null}
          </div>
          <div className="cz-ticket__perforation" aria-hidden="true" />
          <div className="cz-ticket__times">
            <div><span>In</span><strong>{fmtDt(draft.start)}</strong></div>
            <div><span>Out</span><strong>{fmtDt(draft.end)}</strong></div>
          </div>
        </div>

        <div className="citizen-card citizen-summary-card">
          {price && price.base != null && price.discount ? (
            <>
              <SummaryRow label="Subtotal" value={"₹" + price.base} />
              <SummaryRow
                label={"Discount" + (couponApplied ? " (" + couponApplied + ")" : "")}
                value={"−₹" + price.discount}
              />
            </>
          ) : null}
          {total != null ? (
            <SummaryRow label="Total" value={"₹" + total} highlight />
          ) : null}
        </div>

        {/* ── Coupon ── */}
        <div className="citizen-form-section">
          <div className="citizen-form-section__title">Have a coupon?</div>
          <div className="citizen-card">
            {couponApplied ? (
              <div className="citizen-coupon-applied">
                <div>
                  <strong>{couponApplied}</strong>
                  <p className="cz-coupon-applied-sub">Coupon applied — saving ₹{price && price.discount ? price.discount : "—"}</p>
                </div>
                <button
                  type="button"
                  className="citizen-btn citizen-btn--outline"
                  onClick={removeCoupon}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="citizen-coupon-row">
                <input
                  className="citizen-input"
                  placeholder="Enter coupon code"
                  value={coupon}
                  disabled={busy || couponBusy}
                  onChange={function (e) { setCoupon(e.target.value.toUpperCase()); }}
                  onKeyDown={function (e) { if (e.key === "Enter") applyCoupon(); }}
                />
                <button
                  type="button"
                  className="citizen-btn citizen-btn--outline"
                  onClick={applyCoupon}
                  disabled={busy || couponBusy || !coupon.trim()}
                >
                  {couponBusy ? "Applying…" : "Apply"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <StickyFooter>
        <button
          type="button"
          className="citizen-btn citizen-btn--accent citizen-btn--block"
          onClick={pay}
          disabled={busy}
        >
          {busy ? (
            <><span className="citizen-spinner citizen-spinner--sm citizen-spinner--inline" aria-hidden="true" /> Processing…</>
          ) : total != null ? (
            "Pay ₹" + total + " and get my pass"
          ) : (
            "Pay and get my pass"
          )}
        </button>
      </StickyFooter>
    </CitizenLayout>
  );
}

export default function BookingSummaryPage() {
  return (
    <RequireAuth>
      <SummaryContent />
    </RequireAuth>
  );
}
